'use client';

import { type FC, useEffect, useMemo } from 'react';

import { useIsClient } from '../../hooks/use-is-client';
import { useVisibilityChange } from '../../hooks/use-visibility-change';
import { ToastProviderContext } from './context/toast-provider.context';
import { useToastQueue } from './hooks/use-toast-queue';
import { ToastProviderLiveAnnouncer } from './toast-provider-live-announcer';
import type { ToastProviderRootPropsInterface } from './types/toast-provider-root.types';

const DEFAULT_DURATION: NonNullable<ToastProviderRootPropsInterface['duration']> = 0;

/**
 * ToastProvider.Root manages the Toast queue and creates context
 * accessible via `useToast()`
 */
export const ToastProviderRoot: FC<ToastProviderRootPropsInterface> = (props) => {
  const {
    children,
    maxSize = 3,
    pauseOnMouseEnter = true,
    pauseOnWindowBlur = true,
    placement = 'bottom-end',
    resumeOnMouseLeave = true,
    resumeOnWindowFocus = true,
    duration = DEFAULT_DURATION,
  } = props;

  const {
    enqueue: enqueueToast,
    toasts,
    unfilteredToasts,
    activeIds,
    pendingIds,
    dismiss,
    dismissAll,
    pause,
    pauseAll,
    resume,
    resumeAll,
  } = useToastQueue({
    ...props,
    duration,
    placement,
    maxSize,
  });

  const isDocumentVisible = useVisibilityChange();
  const isClient = useIsClient();

  useEffect(() => {
    if (isClient) {
      if (pauseOnWindowBlur && !isDocumentVisible) {
        pauseAll?.();
      }
      if (resumeOnWindowFocus && isDocumentVisible) {
        resumeAll?.();
      }
    }
  }, [
    activeIds,
    pauseOnWindowBlur,
    pauseAll,
    resumeAll,
    resumeOnWindowFocus,
    isDocumentVisible,
    isClient,
  ]);

  const contextValue = useMemo(
    () => ({
      toasts,
      unfilteredToasts,
      activeIds,
      pendingIds,
      dismiss,
      dismissAll,
      pause,
      pauseAll,
      resume,
      resumeAll,
      enqueueToast,
      placement,
      pauseOnMouseEnter,
      resumeOnMouseLeave,
    }),
    [
      toasts,
      unfilteredToasts,
      activeIds,
      pendingIds,
      dismiss,
      dismissAll,
      pause,
      pauseAll,
      resume,
      resumeAll,
      enqueueToast,
      placement,
      pauseOnMouseEnter,
      resumeOnMouseLeave,
    ],
  );

  return (
    <ToastProviderContext.Provider value={contextValue}>
      {children}
      <ToastProviderLiveAnnouncer
        activeIds={activeIds}
        dismiss={dismiss}
        toasts={unfilteredToasts}
      />
    </ToastProviderContext.Provider>
  );
};

ToastProviderRoot.displayName = 'ToastProvider.Root';
